<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import SettingsLayout from '../settings/SettingsLayout.vue';
import HotelPortalAPI from 'dashboard/api/hotelPortal';
import HotelResultCard from './HotelResultCard.vue';
import HotelSearchProgress from './HotelSearchProgress.vue';
import {
  DIST_ORDER,
  DIST_ICONS,
  districtI18nKeys,
  mealCat,
  getDistrict,
  getView,
  VIEW_I18N_KEY,
  SOURCE_META,
  fP,
} from 'dashboard/helper/hotelPortalHelpers';

const MEAL_FILTER_OPTIONS = [
  { value: 'Room only', i18nKey: 'HOTEL_PORTAL.MEALS.ROOM_ONLY' },
  { value: 'Breakfast', i18nKey: 'HOTEL_PORTAL.MEALS.BREAKFAST' },
  { value: 'Half Board', i18nKey: 'HOTEL_PORTAL.MEALS.HALF_BOARD' },
  { value: 'Full Board', i18nKey: 'HOTEL_PORTAL.MEALS.FULL_BOARD' },
];

const VIEW_FILTER_OPTIONS = [
  { value: 'haram', i18nKey: VIEW_I18N_KEY.haram },
  { value: 'city', i18nKey: VIEW_I18N_KEY.city },
  { value: 'mountain', i18nKey: VIEW_I18N_KEY.mountain },
  { value: 'pool', i18nKey: VIEW_I18N_KEY.pool },
];

const MAKKAH_DISTRICTS = [
  'برج الساعة',
  'جبل عمر',
  'جبل الكعبة',
  'اجياد',
  'النسيم',
  'العزيزية',
];

const defaultDates = () => {
  const checkIn = new Date();
  checkIn.setDate(checkIn.getDate() + 7);
  const checkOut = new Date(checkIn);
  checkOut.setDate(checkOut.getDate() + 4);
  const fmt = d => d.toISOString().slice(0, 10);
  return { checkIn: fmt(checkIn), checkOut: fmt(checkOut) };
};

const blankManualForm = () => ({
  hotel: '',
  city: 'مكة المكرمة',
  room: '',
  meal: '',
  nights: 1,
  net: null,
});

export default {
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    NextButton,
    ComboBox,
    HotelResultCard,
    HotelSearchProgress,
  },
  data() {
    const { checkIn, checkOut } = defaultDates();
    return {
      city: 'مكة المكرمة',
      hotelName: '',
      knownHotels: [],
      checkIn,
      checkOut,
      adults: 2,
      rooms: 1,

      isSearching: false,
      searchError: '',
      hasSearched: false,
      allResults: [],
      lastSearchedCity: '',
      searchMeta: null,
      searchStatus: '',
      suppliers: {},
      searchTimer: null,
      searchController: null,
      searchGeneration: 0,

      budgetMin: null,
      budgetMax: null,
      starsFilter: 0,
      mealsFilter: [],
      sourceFilter: '',
      districtFilter: '',
      viewsFilter: [],
      sortBy: 'price',

      showManualQuote: false,
      manualForm: blankManualForm(),
      manualResult: null,
      manualError: '',
      isCalculatingManual: false,

      mealFilterOptions: MEAL_FILTER_OPTIONS,
      viewFilterOptions: VIEW_FILTER_OPTIONS,
      makkahDistricts: MAKKAH_DISTRICTS,
      distIcons: DIST_ICONS,
    };
  },
  computed: {
    allSuppliersFailed() {
      const suppliers = Object.values(this.suppliers);
      return (
        suppliers.length > 0 &&
        suppliers.every(supplier => supplier.state === 'failed')
      );
    },
    isMakkah() {
      return /مكة|مكه|مكرمة/i.test(this.lastSearchedCity);
    },
    hotelComboOptions() {
      return this.knownHotels.map(h => ({
        value: h.name_ar,
        label:
          h.name_en && h.name_en !== h.name_ar
            ? `${h.name_ar} (${h.name_en})`
            : h.name_ar,
      }));
    },
    filteredResults() {
      let list = [...this.allResults];
      if (this.sourceFilter)
        list = list.filter(r => r.source === this.sourceFilter);
      if (this.hotelName.trim()) {
        const q = this.hotelName.trim().toLowerCase();
        list = list.filter(
          r =>
            (r.hotel_name || '').toLowerCase().includes(q) ||
            (r.hotel_name_en || '').toLowerCase().includes(q)
        );
      }
      if (this.starsFilter > 0)
        list = list.filter(r => (r.stars || 0) >= this.starsFilter);
      if (this.budgetMin > 0)
        list = list.filter(r => (r.per_night || 0) >= this.budgetMin);
      if (this.budgetMax > 0)
        list = list.filter(r => (r.per_night || 0) <= this.budgetMax);
      if (this.mealsFilter.length) {
        list = list.filter(r => this.mealsFilter.includes(mealCat(r.meal)));
      }
      if (this.districtFilter) {
        list = list.filter(
          r =>
            getDistrict(r.hotel_name || r.hotel_name_en, r.district) ===
            this.districtFilter
        );
      }
      if (this.viewsFilter.length) {
        list = list.filter(r =>
          this.viewsFilter.includes(getView(r.room_type, r.hotel_name))
        );
      }
      const sorted = [...list];
      if (this.sortBy === 'price')
        sorted.sort((a, b) => (a.per_night || 0) - (b.per_night || 0));
      else if (this.sortBy === 'price_desc')
        sorted.sort((a, b) => (b.per_night || 0) - (a.per_night || 0));
      else if (this.sortBy === 'hotel') {
        sorted.sort((a, b) =>
          (a.hotel_name || '').localeCompare(b.hotel_name || '', 'ar')
        );
      } else if (this.sortBy === 'stars_desc') {
        sorted.sort((a, b) => (b.stars || 0) - (a.stars || 0));
      }
      return sorted;
    },
    sourceCounts() {
      const counts = { 'Violet B2B': 0, Mawasim: 0, Anjum: 0, BillBed: 0 };
      this.filteredResults.forEach(r => {
        if (counts[r.source] != null) counts[r.source] += 1;
      });
      return counts;
    },
    groupedByDistrict() {
      if (!this.isMakkah || this.districtFilter || this.sourceFilter)
        return null;
      const groups = {};
      this.filteredResults.forEach(r => {
        const d =
          getDistrict(r.hotel_name || r.hotel_name_en, r.district) || 'أخرى';
        if (!groups[d]) groups[d] = [];
        groups[d].push(r);
      });
      return DIST_ORDER.map(code => ({
        code,
        icon: DIST_ICONS[code],
        i18n: districtI18nKeys(code),
        items: groups[code] || [],
      })).filter(g => g.items.length);
    },
    searchMetaBadge() {
      if (!this.searchMeta) return '';
      if (this.searchMeta.fromCache) return this.$t('HOTEL_PORTAL.FROM_CACHE');
      if (this.searchMeta.ms)
        return `${(this.searchMeta.ms / 1000).toFixed(1)}s`;
      return '';
    },
    sourceMeta() {
      return SOURCE_META;
    },
  },
  watch: {
    city: {
      immediate: true,
      handler() {
        this.loadKnownHotels();
      },
    },
  },
  beforeUnmount() {
    this.stopSearchPolling();
  },
  methods: {
    stopSearchPolling() {
      this.searchGeneration += 1;
      clearTimeout(this.searchTimer);
      this.searchController?.abort();
      this.searchTimer = null;
    },
    applySearchUpdate(data, startedAt) {
      if (!data.ok || data.status === 'failed') {
        throw new Error(this.$t('HOTEL_PORTAL.UNKNOWN_ERROR'));
      }
      this.allResults = data.results || [];
      this.suppliers = data.suppliers || {};
      this.searchStatus = data.status;
      this.searchMeta = {
        fromCache: data.from_cache,
        ageHours: data.cache_age_hours,
        ms: Date.now() - startedAt,
      };
      this.isSearching = ['queued', 'running'].includes(data.status);
    },
    async pollSearch(id, startedAt, generation, failures = 0) {
      if (generation !== this.searchGeneration) return;
      try {
        const { data } = await HotelPortalAPI.searchStatus(
          id,
          this.searchController.signal
        );
        if (generation !== this.searchGeneration) return;
        this.applySearchUpdate(data, startedAt);
        if (this.isSearching)
          this.scheduleSearchPoll(id, startedAt, generation);
      } catch (error) {
        if (generation !== this.searchGeneration) return;
        if (failures < 2 && (!error.response || error.response.status >= 500)) {
          this.scheduleSearchPoll(id, startedAt, generation, failures + 1);
          return;
        }
        this.isSearching = false;
        this.searchStatus = 'failed';
        this.searchError = this.$t('HOTEL_PORTAL.PROGRESS.INTERRUPTED');
      }
    },
    scheduleSearchPoll(id, startedAt, generation, failures = 0) {
      if (Date.now() - startedAt > 600000) {
        this.isSearching = false;
        this.searchError = this.$t('HOTEL_PORTAL.PROGRESS.INTERRUPTED');
        return;
      }
      this.searchTimer = setTimeout(
        () => this.pollSearch(id, startedAt, generation, failures),
        failures ? 3000 : 1500
      );
    },
    fP,
    districtI18nKeys,
    async loadKnownHotels() {
      try {
        const { data } = await HotelPortalAPI.hotels(this.city);
        this.knownHotels = data.hotels || [];
      } catch (error) {
        this.knownHotels = [];
      }
    },
    async doSearch() {
      if (!this.checkIn || !this.checkOut) {
        useAlert(this.$t('HOTEL_PORTAL.MISSING_DATES'));
        return;
      }
      if (this.checkIn >= this.checkOut) {
        useAlert(this.$t('HOTEL_PORTAL.INVALID_DATES'));
        return;
      }
      if (this.isSearching) return;
      this.stopSearchPolling();
      this.searchController = new AbortController();
      const generation = this.searchGeneration;
      this.isSearching = true;
      this.searchStatus = 'queued';
      this.suppliers = {};
      this.searchError = '';
      this.allResults = [];
      this.searchMeta = null;
      this.hasSearched = true;
      this.lastSearchedCity = this.city;
      const startedAt = Date.now();
      try {
        const { data } = await HotelPortalAPI.startSearch(
          {
            city: this.city,
            check_in: this.checkIn,
            check_out: this.checkOut,
            adults: this.adults,
            rooms: this.rooms,
            children: 0,
          },
          this.searchController.signal
        );
        if (generation !== this.searchGeneration) return;
        this.applySearchUpdate(data, startedAt);
        if (this.isSearching)
          this.scheduleSearchPoll(data.id, startedAt, generation);
      } catch (error) {
        if (generation !== this.searchGeneration) return;
        this.searchError = this.$t(
          error.response?.status === 429
            ? 'HOTEL_PORTAL.PROGRESS.BUSY'
            : 'HOTEL_PORTAL.PROGRESS.INTERRUPTED'
        );
        this.isSearching = false;
        this.searchStatus = 'failed';
      }
    },
    toggleMeal(value) {
      const idx = this.mealsFilter.indexOf(value);
      if (idx === -1) this.mealsFilter.push(value);
      else this.mealsFilter.splice(idx, 1);
    },
    toggleView(value) {
      const idx = this.viewsFilter.indexOf(value);
      if (idx === -1) this.viewsFilter.push(value);
      else this.viewsFilter.splice(idx, 1);
    },
    setSource(value) {
      this.sourceFilter = this.sourceFilter === value ? '' : value;
    },
    async calcManualQuote() {
      const { hotel, city, room, meal, nights, net } = this.manualForm;
      if (!hotel.trim() || !net || net <= 0) {
        useAlert(this.$t('HOTEL_PORTAL.MANUAL.MISSING_FIELDS'));
        return;
      }
      this.isCalculatingManual = true;
      this.manualError = '';
      this.manualResult = null;
      try {
        const { data } = await HotelPortalAPI.manualQuote({
          hotel_name: hotel,
          city,
          room_type: room,
          meal,
          nights,
          net_total: net,
        });
        if (!data.ok) {
          this.manualError =
            data.error || this.$t('HOTEL_PORTAL.UNKNOWN_ERROR');
          return;
        }
        this.manualResult = { ...data, hotel, room, meal };
      } catch (error) {
        this.manualError = this.$t('HOTEL_PORTAL.UNKNOWN_ERROR');
      } finally {
        this.isCalculatingManual = false;
      }
    },
    async copyManualQuote() {
      const r = this.manualResult;
      if (!r) return;
      const text = `${r.hotel} — ${r.room || 'غرفة'} (${r.meal || ''}) — ${r.per_night} ر.س/ليلة — الإجمالي: ${r.sell_total} ر.س لـ ${r.nights} ليالٍ`;
      try {
        await navigator.clipboard.writeText(text);
        useAlert(this.$t('HOTEL_PORTAL.COPIED'));
      } catch (error) {
        useAlert(this.$t('HOTEL_PORTAL.COPY_FAILED'));
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full overflow-y-auto px-6 py-6">
  <SettingsLayout :is-loading="false">
    <template #header>
      <BaseSettingsHeader
        :title="$t('HOTEL_PORTAL.TITLE')"
        :description="$t('HOTEL_PORTAL.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-5 max-w-5xl">
        <!-- Search -->
        <section class="hp-card">
          <div class="grid gap-4 md:grid-cols-6">
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.CITY') }}</label>
              <select v-model="city" class="reset-base hp-input">
                <option value="مكة المكرمة">🕋 {{ $t('HOTEL_PORTAL.CITIES.MAKKAH') }}</option>
                <option value="المدينة المنورة">🕌 {{ $t('HOTEL_PORTAL.CITIES.MADINAH') }}</option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5 md:col-span-2">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.HOTEL_NAME') }}</label>
              <ComboBox
                v-model="hotelName"
                :options="hotelComboOptions"
                :placeholder="$t('HOTEL_PORTAL.FORM.HOTEL_NAME_PLACEHOLDER')"
                :search-placeholder="$t('HOTEL_PORTAL.FORM.HOTEL_NAME_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.CHECK_IN') }}</label>
              <input v-model="checkIn" type="date" class="hp-input" />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.CHECK_OUT') }}</label>
              <input v-model="checkOut" type="date" class="hp-input" />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.ADULTS') }}</label>
              <input v-model.number="adults" type="number" min="1" max="20" class="hp-input" />
            </div>
          </div>
          <div class="flex items-end justify-between mt-4">
            <div class="flex flex-col gap-1.5 w-24">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.ROOMS') }}</label>
              <input v-model.number="rooms" type="number" min="1" max="10" class="hp-input" />
            </div>
            <NextButton
              :label="$t('HOTEL_PORTAL.FORM.SEARCH')"
              icon="i-lucide-search"
              size="lg"
              :is-loading="isSearching"
              @click="doSearch"
            />
          </div>
          <NextButton
            :label="$t('HOTEL_PORTAL.MANUAL.TOGGLE')"
            variant="outline"
            slate
            size="sm"
            class="mt-4"
            @click="showManualQuote = !showManualQuote"
          />
        </section>

        <!-- Manual quote panel -->
        <section v-if="showManualQuote" class="hp-card">
          <p class="text-sm text-n-slate-11 mb-4">{{ $t('HOTEL_PORTAL.MANUAL.HINT') }}</p>
          <div class="grid gap-4 md:grid-cols-6">
            <div class="flex flex-col gap-1.5 md:col-span-2">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.MANUAL.HOTEL') }}</label>
              <input
                v-model="manualForm.hotel"
                type="text"
                class="hp-input"
                :placeholder="$t('HOTEL_PORTAL.MANUAL.HOTEL_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.FORM.CITY') }}</label>
              <select v-model="manualForm.city" class="reset-base hp-input">
                <option value="مكة المكرمة">{{ $t('HOTEL_PORTAL.CITIES.MAKKAH') }}</option>
                <option value="المدينة المنورة">{{ $t('HOTEL_PORTAL.CITIES.MADINAH') }}</option>
              </select>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.MANUAL.ROOM') }}</label>
              <input v-model="manualForm.room" type="text" class="hp-input" placeholder="Deluxe Haram View" />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.MANUAL.MEAL') }}</label>
              <input
                v-model="manualForm.meal"
                type="text"
                class="hp-input"
                :placeholder="$t('HOTEL_PORTAL.MANUAL.MEAL_PLACEHOLDER')"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.MANUAL.NIGHTS') }}</label>
              <input v-model.number="manualForm.nights" type="number" min="1" max="30" class="hp-input" />
            </div>
          </div>
          <div class="flex items-end gap-3 mt-4">
            <div class="flex flex-col gap-1.5 flex-1 max-w-xs">
              <label class="hp-label">{{ $t('HOTEL_PORTAL.MANUAL.NET_TOTAL') }}</label>
              <input v-model.number="manualForm.net" type="number" min="1" step="1" class="hp-input" />
            </div>
            <NextButton
              :label="$t('HOTEL_PORTAL.MANUAL.CALCULATE')"
              :is-loading="isCalculatingManual"
              @click="calcManualQuote"
            />
          </div>

          <div v-if="manualError" class="mt-4 text-n-ruby-11 text-sm">⚠️ {{ manualError }}</div>
          <div v-else-if="manualResult" class="mt-4 p-4 rounded-lg bg-n-slate-3 border border-dashed border-n-weak max-w-md">
            <div v-if="manualResult.zone_name_ar" class="text-[10px] text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 mb-2 inline-block">
              📍 {{ manualResult.zone_name_ar }} ({{ $t('HOTEL_PORTAL.CARD.ZONE_PRIORITY', { priority: manualResult.zone_priority }) }})
            </div>
            <div class="flex justify-between text-sm text-n-slate-11 py-1">
              <span>{{ $t('HOTEL_PORTAL.MANUAL.ORIGINAL_PRICE') }}</span>
              <b class="text-n-slate-12">{{ fP(manualResult.net_total) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
            </div>
            <div class="flex justify-between text-sm text-n-slate-11 py-1">
              <span>
                {{ manualResult.margin_minimum
                  ? $t('HOTEL_PORTAL.MANUAL.MARGIN_WITH_MIN', { percent: manualResult.margin_percent, amount: fP(manualResult.margin_minimum) })
                  : $t('HOTEL_PORTAL.MANUAL.MARGIN', { percent: manualResult.margin_percent }) }}
              </span>
              <b class="text-amber-600">+{{ fP(manualResult.margin_amount) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
            </div>
            <div class="flex justify-between text-sm pt-2 mt-1 border-t border-n-weak">
              <span class="text-n-slate-12 font-medium">{{ $t('HOTEL_PORTAL.MANUAL.FINAL_PRICE', { rate: manualResult.per_night }) }}</span>
              <b class="text-n-slate-12">{{ fP(manualResult.sell_total) }} {{ $t('HOTEL_PORTAL.CURRENCY') }}</b>
            </div>
            <NextButton
              :label="$t('HOTEL_PORTAL.COPY_PRICE')"
              size="sm"
              faded
              amber
              class="w-full mt-3"
              @click="copyManualQuote"
            />
          </div>
        </section>

        <!-- Filters (sidebar) + Results -->
        <div v-if="hasSearched" class="flex gap-5 items-start">
          <!-- Filters sit first in DOM order so they land on the natural
               reading-start side - right in Arabic (RTL), left in English
               (LTR) - matching how every travel-booking site places its
               filter panel, without needing separate RTL/LTR overrides. -->
          <aside class="hp-card w-64 flex-shrink-0 flex flex-col gap-5 sticky top-4">
            <h3 class="hp-section-title !mb-0">{{ $t('HOTEL_PORTAL.FILTERS.LABEL') }}</h3>

            <div>
              <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.BUDGET') }}</div>
              <div class="flex items-center gap-2">
                <input
                  v-model.number="budgetMin"
                  type="number"
                  min="0"
                  step="50"
                  class="hp-input"
                  :placeholder="$t('HOTEL_PORTAL.FILTERS.FROM')"
                />
                <span class="text-n-slate-10">—</span>
                <input
                  v-model.number="budgetMax"
                  type="number"
                  min="0"
                  step="50"
                  class="hp-input"
                  :placeholder="$t('HOTEL_PORTAL.FILTERS.TO')"
                />
              </div>
            </div>

            <div>
              <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.STARS') }}</div>
              <div class="flex gap-1.5 flex-wrap">
                <button
                  v-for="opt in [0, 3, 4, 5]"
                  :key="opt"
                  class="hp-chip"
                  :class="{ 'hp-chip-active': starsFilter === opt }"
                  @click="starsFilter = opt"
                >
                  {{ opt === 0 ? $t('HOTEL_PORTAL.FILTERS.ALL') : `+${opt} ★` }}
                </button>
              </div>
            </div>

            <div>
              <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.MEALS') }}</div>
              <div class="flex gap-1.5 flex-wrap">
                <button
                  v-for="opt in mealFilterOptions"
                  :key="opt.value"
                  class="hp-chip"
                  :class="{ 'hp-chip-active': mealsFilter.includes(opt.value) }"
                  @click="toggleMeal(opt.value)"
                >
                  {{ $t(opt.i18nKey) }}
                </button>
              </div>
            </div>

            <div>
              <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.SOURCE') }}</div>
              <div class="flex gap-1.5 flex-wrap">
                <button
                  class="hp-chip"
                  :class="{ 'hp-chip-active': !sourceFilter }"
                  @click="sourceFilter = ''"
                >
                  {{ $t('HOTEL_PORTAL.FILTERS.ALL') }}
                </button>
                <button
                  v-for="(meta, key) in sourceMeta"
                  :key="key"
                  class="hp-chip"
                  :class="{ 'hp-chip-active': sourceFilter === key }"
                  @click="setSource(key)"
                >
                  {{ meta.dot }} {{ $t(meta.i18nKey) }}
                </button>
              </div>
            </div>

            <template v-if="isMakkah">
              <div class="border-t border-n-weak" />
              <div>
                <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.DISTRICT') }}</div>
                <div class="flex gap-1.5 flex-wrap">
                  <button
                    class="hp-chip"
                    :class="{ 'hp-chip-active': !districtFilter }"
                    @click="districtFilter = ''"
                  >
                    {{ $t('HOTEL_PORTAL.FILTERS.ALL') }}
                  </button>
                  <button
                    v-for="code in makkahDistricts"
                    :key="code"
                    class="hp-chip"
                    :class="{ 'hp-chip-active': districtFilter === code }"
                    @click="districtFilter = districtFilter === code ? '' : code"
                  >
                    {{ $t(districtI18nKeys(code).label) }}
                  </button>
                </div>
              </div>
              <div>
                <div class="hp-label mb-2">{{ $t('HOTEL_PORTAL.FILTERS.VIEW') }}</div>
                <div class="flex gap-1.5 flex-wrap">
                  <button
                    v-for="opt in viewFilterOptions"
                    :key="opt.value"
                    class="hp-chip"
                    :class="{ 'hp-chip-active': viewsFilter.includes(opt.value) }"
                    @click="toggleView(opt.value)"
                  >
                    {{ $t(opt.i18nKey) }}
                  </button>
                </div>
              </div>
            </template>
          </aside>

          <div class="flex-1 min-w-0 flex flex-col gap-4">
            <HotelSearchProgress
              v-if="isSearching || Object.keys(suppliers).length"
              :status="searchStatus"
              :suppliers="suppliers"
              :result-count="allResults.length"
            />
            <p v-if="searchError && allResults.length" role="alert" class="text-sm text-n-ruby-11">
              {{ searchError }}
            </p>
            <!-- Toolbar -->
            <div v-if="!isSearching || allResults.length" class="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div class="text-sm font-bold text-n-slate-12">
                  {{ $t('HOTEL_PORTAL.RESULTS_COUNT', { count: filteredResults.length }) }}
                  <span v-if="searchMetaBadge" class="text-xs text-n-slate-11 font-normal">{{ searchMetaBadge }}</span>
                </div>
                <div class="text-xs text-n-slate-11 flex gap-3 flex-wrap mt-1">
                  <span v-for="(count, source) in sourceCounts" :key="source">
                    <template v-if="count > 0">{{ $t(sourceMeta[source]?.i18nKey) }}: {{ count }}</template>
                  </span>
                </div>
              </div>
              <select v-model="sortBy" class="reset-base hp-input !w-auto text-xs">
                <option value="price">{{ $t('HOTEL_PORTAL.SORT.PRICE_ASC') }}</option>
                <option value="price_desc">{{ $t('HOTEL_PORTAL.SORT.PRICE_DESC') }}</option>
                <option value="hotel">{{ $t('HOTEL_PORTAL.SORT.NAME') }}</option>
                <option value="stars_desc">{{ $t('HOTEL_PORTAL.SORT.STARS') }}</option>
              </select>
            </div>

            <!-- Results / states -->
            <div v-if="isSearching && !allResults.length" class="hp-card py-16 text-center text-n-slate-11">
              <div class="hp-spinner mx-auto mb-4" />
              <div class="text-n-slate-12 font-bold mb-1">{{ $t('HOTEL_PORTAL.SEARCHING') }}</div>
              <div class="text-sm">{{ $t('HOTEL_PORTAL.SEARCHING_HINT') }}</div>
            </div>

            <div v-else-if="(searchError || allSuppliersFailed) && !allResults.length" class="hp-card py-16 text-center">
              <div class="text-4xl mb-3">⚠️</div>
              <div class="text-n-slate-12 font-bold mb-1">{{ $t('HOTEL_PORTAL.SEARCH_FAILED') }}</div>
              <div class="text-sm text-n-slate-11 mb-4">{{ searchError || $t('HOTEL_PORTAL.PROGRESS.ALL_FAILED') }}</div>
              <NextButton :label="$t('HOTEL_PORTAL.RETRY')" size="sm" @click="doSearch" />
            </div>

            <div v-else-if="!filteredResults.length" class="hp-card py-16 text-center text-n-slate-11">
              <div class="text-4xl mb-3">🔍</div>
              <div class="text-n-slate-12 font-bold mb-1">{{ $t('HOTEL_PORTAL.NO_RESULTS') }}</div>
              <div class="text-sm max-w-md mx-auto">
                {{ hotelName.trim()
                  ? $t('HOTEL_PORTAL.NO_RESULTS_HOTEL_NAME_HINT', { query: hotelName })
                  : $t('HOTEL_PORTAL.NO_RESULTS_HINT') }}
              </div>
            </div>

            <template v-else-if="groupedByDistrict">
              <div v-for="group in groupedByDistrict" :key="group.code" class="mb-2">
                <div class="flex items-center gap-2.5 mb-3 px-4 py-2.5 bg-n-solid-2 border border-n-weak rounded-lg">
                  <span class="text-xl">{{ group.icon }}</span>
                  <div>
                    <div class="text-sm font-bold text-n-slate-12">
                      {{ $t('HOTEL_PORTAL.GROUP_HOTELS_IN', { district: $t(group.i18n.label) }) }}
                    </div>
                    <div class="text-xs text-n-slate-11">{{ $t(group.i18n.desc) }}</div>
                  </div>
                  <span class="text-xs text-n-slate-11 bg-n-slate-3 border border-n-weak rounded-lg px-2 py-0.5 ml-auto">
                    {{ $t('HOTEL_PORTAL.RESULTS_COUNT', { count: group.items.length }) }}
                  </span>
                </div>
                <div class="grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(255px, 1fr))">
                  <HotelResultCard
                    v-for="(item, idx) in group.items"
                    :key="idx"
                    :result="item"
                    :city="lastSearchedCity"
                  />
                </div>
              </div>
            </template>

            <div v-else class="grid gap-3" style="grid-template-columns: repeat(auto-fill, minmax(255px, 1fr))">
              <HotelResultCard
                v-for="(item, idx) in filteredResults"
                :key="idx"
                :result="item"
                :city="lastSearchedCity"
              />
            </div>
          </div>
        </div>

        <div v-else class="hp-card py-16 text-center text-n-slate-11">
          <div class="text-4xl mb-3">🏨</div>
          <div class="text-n-slate-12 font-bold mb-1">{{ $t('HOTEL_PORTAL.EMPTY_TITLE') }}</div>
          <div class="text-sm max-w-sm mx-auto">{{ $t('HOTEL_PORTAL.EMPTY_HINT') }}</div>
        </div>
      </div>
    </template>
  </SettingsLayout>
  </div>
</template>

<style scoped>
.hp-card {
  background: var(--n-solid-1, #fff);
  border: 1px solid var(--n-weak, #e0e0e0);
  border-radius: 12px;
  padding: 20px;
}
.hp-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--n-slate-11, #666);
}
.hp-section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--n-slate-12, #111);
  margin-bottom: 16px;
}
.hp-input {
  background: var(--n-solid-1, #fff);
  border: 1.5px solid var(--n-weak, #e0e0e0);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  width: 100%;
  outline: none;
}
.hp-input:focus {
  border-color: rgb(180, 136, 28);
}
.hp-chip {
  padding: 6px 14px;
  border: 1.5px solid var(--n-weak, #e0e0e0);
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--n-slate-11, #666);
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.12s ease;
}
.hp-chip:hover {
  border-color: var(--n-slate-9, #999);
  color: var(--n-slate-12, #111);
}
.hp-chip-active {
  border-color: rgb(180, 136, 28);
  background: rgba(180, 136, 28, 0.12);
  color: rgb(148, 111, 20);
  font-weight: 700;
}
.hp-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--n-weak, #e0e0e0);
  border-top-color: rgb(180, 136, 28);
  border-radius: 50%;
  animation: hp-spin 0.7s linear infinite;
}
@keyframes hp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
